/**
 * D1 北极星指标看板（Phase 5-2 / Design §5.2 D1）
 *
 * KPI: DAU / WAU / MAU / 新增 / 留存
 * 切片: 周期切换（day/week/month/quarter/year）+ 日期范围 + 渠道（可选）
 * 导出: CSV (含水印)
 *
 * 权限: PermissionGate(DASHBOARD_READ) — support 直接 fallback。
 */
import { useState } from 'react';
import { Card, Row, Col, Statistic, Space, Alert, Result, Input } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { ChartContainer } from '../../components/dashboard/ChartContainer';
import { TimeSlicer, type TimeSlicerValue } from '../../components/dashboard/TimeSlicer';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { PERMISSIONS } from '../../contracts/permissions';
import { getNorthStar, type NorthStarFilter } from '../../api/dashboard';

const DEFAULT_RANGE_DAYS = 30;
function defaultRange(): { startAt: string; endAt: string } {
  const end = new Date();
  const start = new Date(end.getTime() - DEFAULT_RANGE_DAYS * 24 * 3600 * 1000);
  return { startAt: start.toISOString(), endAt: end.toISOString() };
}

function NorthStarInner() {
  const initial = defaultRange();
  const [slicer, setSlicer] = useState<TimeSlicerValue>({
    period: 'day',
    startAt: initial.startAt,
    endAt: initial.endAt,
  });
  const [channel, setChannel] = useState<string>('');

  const filter: NorthStarFilter = {
    period: slicer.period,
    startAt: slicer.startAt,
    endAt: slicer.endAt,
    channel: channel || undefined,
  };

  const query = useQuery({
    queryKey: ['dashboard', 'north-star', filter],
    queryFn: () => getNorthStar(filter),
  });

  const data = query.data;
  const isEmpty = !!data && data.newUsersSeries.length === 0;

  // ECharts option: 双线 — 新增 + 活跃
  const trendOption = data
    ? {
        tooltip: { trigger: 'axis' as const },
        legend: { data: ['新增', '活跃(近似)'] },
        xAxis: {
          type: 'category' as const,
          data: Array.from(
            new Set([
              ...data.newUsersSeries.map((p) => p.bucket),
              ...data.activeUsersSeries.map((p) => p.bucket),
            ]),
          ).sort(),
        },
        yAxis: { type: 'value' as const },
        series: [
          {
            name: '新增',
            type: 'line' as const,
            data: data.newUsersSeries.map((p) => p.count),
          },
          {
            name: '活跃(近似)',
            type: 'line' as const,
            data: data.activeUsersSeries.map((p) => p.count),
          },
        ],
      }
    : {};

  // ECharts option: 留存 D1/D7/D30 折线
  const retentionOption = data
    ? {
        tooltip: { trigger: 'axis' as const },
        legend: { data: ['D1', 'D7', 'D30'] },
        xAxis: {
          type: 'category' as const,
          data: data.retention.map((p) => p.cohort),
        },
        yAxis: { type: 'value' as const, max: 1, min: 0 },
        series: [
          { name: 'D1', type: 'line' as const, data: data.retention.map((p) => p.d1) },
          { name: 'D7', type: 'line' as const, data: data.retention.map((p) => p.d7) },
          { name: 'D30', type: 'line' as const, data: data.retention.map((p) => p.d30) },
        ],
      }
    : {};

  return (
    <div data-testid="dashboard-north-star">
      <Card
        title="D1 北极星指标"
        extra={
          <ExportButton kind="north-star" filter={filter} />
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <TimeSlicer value={slicer} onChange={setSlicer} />
            <Input
              placeholder="渠道（如 wechat）"
              allowClear
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              style={{ width: 200 }}
              data-testid="north-star-channel"
            />
          </Space>

          {/* Alert type=warning：notes 显式列举 5 处 updated_at 污染源，特别是
              lifecycle cron 会批量刷历史 user row 导致 DAU 高估，提示更高优先级。 */}
          {data?.notes && <Alert type="warning" message={data.notes} showIcon />}

          {/* 用户规模 KPI：注册总数 + 当日新增（不受时间窗切片影响）。
              当日新增的时间序列复用下方「新增 / 活跃趋势」折线图。 */}
          <Row gutter={16}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="注册用户总数"
                  value={data?.totalUsers ?? 0}
                  loading={query.isLoading}
                  data-testid="north-star-total-users"
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="当日新增用户"
                  value={data?.newUsersToday ?? 0}
                  loading={query.isLoading}
                  data-testid="north-star-new-users-today"
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="DAU (近似)"
                  value={data?.dau ?? 0}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="WAU (近似)"
                  value={data?.wau ?? 0}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="MAU (近似)"
                  value={data?.mau ?? 0}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
          </Row>

          <ChartContainer
            title="新增 / 活跃趋势"
            option={trendOption}
            loading={query.isLoading}
            empty={isEmpty}
            testId="north-star-trend"
          />
          <ChartContainer
            title="留存 D1/D7/D30"
            option={retentionOption}
            loading={query.isLoading}
            empty={!data || data.retention.length === 0}
            testId="north-star-retention"
          />
        </Space>
      </Card>
    </div>
  );
}

export default function NorthStarPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.DASHBOARD_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="数据看板需 dashboard:read 权限。"
        />
      }
    >
      <NorthStarInner />
    </PermissionGate>
  );
}
